import { HStack } from "@chakra-ui/layout";
import { Box, Flex, Text, Button, GridItem } from "@chakra-ui/react";
import { CheckCircleIcon } from "@chakra-ui/icons";

import React, { FC } from "react";
import Card from "../../layout/card";
import Section from "../../form/section";
import { ApplicationType } from "../types";

interface Plan {
  priceLookUpKey: string;
  term: string;
  amount: number;
  disclaimer: string;
}

const defaultMonthDisclaimer =
  "You will be charged on the 1st of each month. Pro-rata calculations will be used on your first payment installation.";
const defaultYearlyDisclaimer =
  "You will be charged on the 1st of each July. Pro-rata calculations will be used on your first payment installation.";

const licensedPlans: Plan[] = [
  {
    amount: 26.42,
    priceLookUpKey: "rei_licensed_monthly",
    term: "Monthly",
    disclaimer: defaultMonthDisclaimer,
  },
  {
    amount: 317.0,
    priceLookUpKey: "rei_licensed_yearly",
    term: "Yearly",
    disclaimer: defaultYearlyDisclaimer,
  },
];

const representativePlans: Plan[] = [
  {
    amount: 17.42,
    priceLookUpKey: "rei_representative_monthly",
    term: "Monthly",
    disclaimer: defaultMonthDisclaimer,
  },
  {
    amount: 209.0,
    priceLookUpKey: "rei_representative_yearly",
    term: "Yearly",
    disclaimer: defaultYearlyDisclaimer,
  },
];

type props = {
  handleSelection: (priceLookUpKey: string) => void;
  selectedPrice?: string;
  backButtonHandler: () => void;
  proceedButtonHandler: () => void;
  applicationType: ApplicationType;
};

const PlansForm: React.FC<props> = ({
  handleSelection,
  selectedPrice,
  proceedButtonHandler,
  backButtonHandler,
  applicationType,
}) => {
  const plans: Plan[] =
    applicationType === ApplicationType.licensedMember
      ? licensedPlans
      : representativePlans;

  return (
    <Card maxWidth="700px">
      <Section columns={4} title="">
        <GridItem colSpan={4}>
          <HStack justify="space-between" display="block">
            {plans.map((plan) => (
              <PlanCard
                key={plan.priceLookUpKey}
                isDisabled={false}
                isSelected={selectedPrice === plan.priceLookUpKey}
                onClick={() => handleSelection(plan.priceLookUpKey)}
                plan={plan}
              />
            ))}
          </HStack>

          <Button
            isFullWidth={true}
            isDisabled={!selectedPrice}
            onClick={() => proceedButtonHandler()}
            colorScheme="brand"
          >
            Proceed
          </Button>
        </GridItem>

        <Button onClick={() => backButtonHandler()}>Back</Button>
      </Section>
    </Card>
  );
};

interface PlanCardProps {
  plan: Plan;
  onClick: (event: React.MouseEvent<any, MouseEvent>) => any;
  isSelected: boolean;
  isDisabled: boolean;
}
const PlanCard: FC<PlanCardProps> = ({
  onClick,
  isSelected,
  isDisabled,
  plan,
}) => {
  const iconSize = 6;
  const { amount, term } = plan;

  return (
    <Box
      onClick={(e) => (!isDisabled ? onClick(e) : undefined)}
      cursor={isDisabled ? "disabled" : "pointer"}
      userSelect="none"
      borderRadius={4}
      _hover={
        isSelected || isDisabled
          ? {}
          : { borderWidth: 1, borderColor: "primary.500" }
      }
      borderWidth={isSelected ? 2 : 1}
      borderColor={isSelected ? "primary.500" : "neutral.200"}
      backgroundColor={isSelected ? "primary.50" : ""}
      padding={4}
      margin="10px!important"
      marginLeft="auto!important"
      marginRight="auto!important"
      width="100%"
      opacity={isDisabled ? 0.5 : 1}
    >
      <Flex flexDirection="column" width="100%">
        {isSelected && (
          <div style={{ position: "relative" }}>
            <CheckCircleIcon
              width={iconSize}
              height={iconSize}
              position="absolute"
              top={0 - 14 / 2}
              right={0 - 14 / 2}
              background="white"
              color="primary.500"
              border="2px solid white"
              borderRadius={iconSize * 2}
            />
          </div>
        )}
        <Text fontWeight={700} fontSize={16}>
          {term}
        </Text>
        <Box flexGrow={1}>
          <Text fontWeight={700} fontSize={24} marginTop={4}>
            {formatCurrency(amount)}
          </Text>
          <Text fontSize={12}>{plan.disclaimer}</Text>
        </Box>
      </Flex>
    </Box>
  );
};

const formatCurrency = (value: number) => {
  return `$${value.toFixed(2)}`;
};

export default PlansForm;
